<template>
  <div class="musubi-page musubi-page--segments">
    <header class="musubi-page__head">
      <h1>Segments</h1>
      <p class="text-muted">
        Saved subscriber filters, scoped to one list. Empty filter = the
        whole list. Active+subscribed gate is always on (no segment can
        accidentally include an unsubscribed address). Used by campaigns +
        drips to target a subset of a list without duplicating recipients.
      </p>
    </header>

    <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>

    <section class="actions-bar">
      <button class="btn btn-outline-primary" @click="openCreate">
        <CygnusIcon name="plus" class="me-1" />New segment
      </button>
      <button class="btn btn-link" :disabled="busy.load" @click="refresh">
        <CygnusIcon name="spinner" :spin="true" v-if="busy.load" />
        <span v-else><CygnusIcon name="refresh" /> Refresh</span>
      </button>
    </section>

    <!-- Editor (create OR edit existing) -->
    <section v-if="editor" class="editor card mb-3">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-start mb-3">
          <h6 class="mb-0">{{ editor.id ? `Edit segment #${editor.id}` : 'New segment' }}</h6>
          <button class="btn btn-link btn-sm" @click="closeEditor">Close</button>
        </div>

        <form @submit.prevent="saveSegment">
          <div class="row g-2 mb-3">
            <div class="col-md-5">
              <label class="form-label small text-muted">Name *</label>
              <input v-model="editor.name" type="text" required class="form-control form-control-sm" placeholder="UK active opt-ins">
            </div>
            <div class="col-md-3">
              <label class="form-label small text-muted">List *</label>
              <MusubiEntitySelect
                v-model="editor.list_id"
                endpoint="/api/v1/admin/lists"
                :option-label="l => l.name"
                placeholder="Select list…" required :disabled="!!editor.id" />
              <div v-if="editor.id" class="form-text small">List can't change after create.</div>
            </div>
            <div class="col-md-4">
              <label class="form-label small text-muted">Description</label>
              <input v-model="editor.description" type="text" class="form-control form-control-sm">
            </div>
          </div>

          <label class="form-label small text-muted mb-2">Filters</label>
          <SegmentFilterBuilder
            v-model="editor.filter_json"
            :list-id="editor.list_id"
            :previewing="busy.preview === 'draft'"
            :last-preview="lastDraftCount"
            @preview="previewDraft"
          />

          <div class="d-flex gap-2 mt-3">
            <button type="submit" class="btn btn-primary btn-sm" :disabled="busy.save || !editor.name || !editor.list_id">
              <CygnusIcon name="spinner" :spin="true" v-if="busy.save" />
              <span v-else>{{ editor.id ? 'Save changes' : 'Create segment' }}</span>
            </button>
            <button
              v-if="editor.id"
              type="button"
              class="btn btn-outline-danger btn-sm ms-auto"
              @click="deleteSegment(editor.id)"
            >
              <CygnusIcon name="trash" class="me-1" />Delete
            </button>
          </div>
        </form>
      </div>
    </section>

    <MusubiTabulator
      :rows="rows"
      :columns="columns"
      storage-key="admin.segments"
      :initial-sort="{ column: 'created', dir: 'desc' }"
      :page-size="25"
      :loading="busy.load"
      :error="error"
      @row-click="onRowClick"
    />

    <p v-if="lastMessage" class="actions-bar__msg mt-2 small">{{ lastMessage }}</p>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";
import MusubiTabulator from "@root/components/MusubiTabulator.vue";
import MusubiEntitySelect from "@root/components/MusubiEntitySelect.vue";
import SegmentFilterBuilder from "@root/components/SegmentFilterBuilder.vue";
import { esc } from "./util/cellHtml";
import cygnusSpriteHtml from "@root/services/icons/cygnusSpriteHtml";

export default {
  name: 'AdminSegments',
  components: { MusubiTabulator, SegmentFilterBuilder, MusubiEntitySelect },
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      rows: [],
      busy: { load: false, save: false, preview: null }, // preview: 'draft' | <id>
      error: null,
      lastMessage: '',
      lastDraftCount: null,
      editor: null, // { id?, name, list_id, description, filter_json: [] }
      // list_id → name, so the table shows "p3-smoke-list" instead of "#8".
      listsById: {},
    };
  },
  computed: {
    columns() {
      const self = this;
      return [
        { key: 'id',                     label: 'ID',         width: 60,  sorter: 'number' },
        { key: 'name',                   label: 'Name' },
        { key: 'list_id',                label: 'List',       width: 150, sorter: 'number',
          format: (v) => `<span title="#${esc(v)}">${esc(self.listsById[v] || `#${v}`)}</span>` },
        { key: 'recipient_count_cached', label: 'Recipients', width: 110, sorter: 'number',
          format: (v, row) => {
            const num = (v == null) ? '<span class="text-muted">—</span>' : esc(v);
            const isBusy = self.busy.preview === row.id;
            const btnTxt = isBusy
              ? cygnusSpriteHtml('spinner', { spin: true })
              : (v == null ? 'Count' : 'Recount');
            return `${num} <button class="btn btn-link btn-sm p-0 ms-2 js-preview" data-id="${esc(row.id)}">${btnTxt}</button>`;
          },
        },
        { key: 'last_evaluated_at',      label: 'Last counted', width: 140, sorter: 'number',
          format: (v) => v ? new Date(v * 1000).toLocaleString() : '—' },
        { key: 'created',                label: 'Created',    width: 140, sorter: 'number',
          format: (v) => v ? new Date(v * 1000).toLocaleString() : '—' },
      ];
    },
  },
  mounted() {
    this.refresh();
    this.loadLists();
    document.addEventListener('click', this.handleDelegatedClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleDelegatedClick);
  },
  methods: {
    async refresh() {
      this.busy.load = true; this.error = null;
      try {
        const res = await this.api.get('/api/v1/admin/segments');
        const payload = res?.data?.data ?? res?.data ?? res;
        this.rows = Array.isArray(payload) ? payload : (payload?.rows || []);
      } catch (e) { this.error = e?.message || 'Failed to load segments.'; }
      this.busy.load = false;
    },
    async loadLists() {
      try {
        const res = await this.api.get('/api/v1/admin/lists');
        const payload = res?.data?.data ?? res?.data ?? res;
        const lists = Array.isArray(payload) ? payload : (payload?.rows || []);
        this.listsById = Object.fromEntries(lists.map((l) => [l.id, l.name || `#${l.id}`]));
      } catch (e) { /* non-fatal — table just falls back to #id */ }
    },
    openCreate() {
      this.editor = { name: '', list_id: null, description: '', filter_json: [] };
      this.lastDraftCount = null;
    },
    closeEditor() {
      this.editor = null;
      this.lastDraftCount = null;
    },
    async onRowClick(row) {
      if (!row?.id) return;
      try {
        const res = await this.api.get(`/api/v1/admin/segments/${row.id}`);
        const payload = res?.data ?? res;
        const seg = payload?.segment;
        if (!seg) return;
        this.editor = {
          id: seg.id,
          name: seg.name,
          list_id: seg.list_id,
          description: seg.description || '',
          filter_json: Array.isArray(seg.filter_json) ? seg.filter_json : [],
        };
        this.lastDraftCount = seg.recipient_count_cached;
      } catch (e) { this.error = e?.message || 'Failed to open segment.'; }
    },
    async previewDraft({ list_id, filter_json }) {
      if (!list_id) { this.error = 'Set a list ID first.'; return; }
      this.busy.preview = 'draft';
      try {
        const res = await this.api.post('/api/v1/admin/segments/preview-draft', { list_id, filter_json });
        const payload = res?.data ?? res;
        this.lastDraftCount = payload?.count ?? null;
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Preview failed.';
      }
      this.busy.preview = null;
    },
    async saveSegment() {
      this.busy.save = true; this.error = null;
      try {
        const body = {
          name: this.editor.name,
          description: this.editor.description || null,
          list_id: this.editor.list_id,
          filter_json: this.editor.filter_json,
        };
        if (this.editor.id) {
          await this.api.put(`/api/v1/admin/segments/${this.editor.id}`, body);
          this.lastMessage = `Segment #${this.editor.id} updated.`;
        } else {
          const res = await this.api.post('/api/v1/admin/segments', body);
          const payload = res?.data ?? res;
          if (payload?.segment?.id) {
            this.lastMessage = `Segment #${payload.segment.id} created.`;
            this.editor.id = payload.segment.id;
          } else {
            this.error = payload?.error || 'Create failed.';
          }
        }
        await this.refresh();
        if (this.editor.id) await this.preview(this.editor.id);
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Save failed.';
      }
      this.busy.save = false;
    },
    async preview(id) {
      this.busy.preview = id; this.lastMessage = '';
      try {
        const res = await this.api.post(`/api/v1/admin/segments/${id}/preview`, {});
        const payload = res?.data ?? res;
        this.lastMessage = `Segment #${id} resolves to ${payload.count} recipient${payload.count === 1 ? '' : 's'}.`;
        if (this.editor?.id === id) this.lastDraftCount = payload.count;
        await this.refresh();
      } catch (e) {
        this.lastMessage = e?.response?.data?.error || e?.message || 'Preview failed.';
      }
      this.busy.preview = null;
    },
    async deleteSegment(id) {
      if (!confirm(`Delete segment #${id}? This won't affect campaigns already using it.`)) return;
      try {
        await this.api.delete(`/api/v1/admin/segments/${id}`);
        this.editor = null;
        this.lastMessage = `Segment #${id} deleted.`;
        await this.refresh();
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Delete failed.';
      }
    },
    handleDelegatedClick(ev) {
      const btn = ev.target.closest('.js-preview');
      if (!btn) return;
      ev.preventDefault();
      ev.stopPropagation();
      const id = Number(btn.dataset.id);
      if (id > 0) this.preview(id);
    },
  },
};
</script>

<style>
.musubi-page--segments { padding: var(--m-space-6, 32px); }
.actions-bar { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; }
.actions-bar__msg { color: var(--m-text-secondary, #475569); }
.editor { background: var(--m-bg-card, #FFF); }
</style>
